import React, { useEffect, useState } from "react";
import { db } from "@/config/firebase";
import { addDoc, collection, getDocs, query, serverTimestamp } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Vote as VoteIcon, Star, Trophy, Beer } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Vote() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [eventId, setEventId] = useState("");
  const [nominee, setNominee] = useState("");
  const [category, setCategory] = useState("game_overperformer");
  const [status, setStatus] = useState("");

  useEffect(() => {
    (async () => {
      const ev = await getDocs(query(collection(db, "events")));
      setEvents(ev.docs.map(d=>({ id:d.id, ...d.data()})));
      const pl = await getDocs(query(collection(db, "players")));
      setPlayers(pl.docs.map(d=>({ id:d.id, ...d.data()})));
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user || role !== "player") { setStatus("Nur Spieler dürfen abstimmen."); return; }
    await addDoc(collection(db, "overperformer_votes"), {
      event_id: eventId,
      voter_user_id: user.uid,
      nominee_player_id: nominee,
      category,
      created_at: serverTimestamp(),
    });
    setStatus("Stimme gespeichert!");
  };

  const categories = [
    { value: 'game_overperformer', label: 'Game Overperformer', icon: Trophy, color: 'text-yellow-400' },
    { value: 'kiosk_overperformer', label: 'Kiosk Overperformer', icon: Beer, color: 'text-amber-400' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-semibold flex items-center gap-2">
              <Star size={18} className="text-yellow-400" />
              Overperformer-Wahl
            </h1>
            <p className="text-xs text-rose-200/50">Wähle den besten Spieler</p>
          </div>
        </div>
      </header>

      <div className="px-3 py-4 space-y-4">
        {/* Info Card */}
        <Card className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border-yellow-500/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <VoteIcon size={24} className="text-yellow-400" />
              <div>
                <p className="text-sm font-medium text-yellow-200">Deine Stimme zählt!</p>
                <p className="text-xs text-yellow-200/60">Wähle den Spieler, der am meisten überzeugt hat</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vote Form */}
        <Card className="bg-[#12090c] border-white/5">
          <CardContent className="pt-4">
            <form onSubmit={submit} className="space-y-4" data-testid="vote-form">
              {/* Event Selection */}
              <div>
                <label className="text-xs text-rose-200/60 block mb-1.5">Event auswählen</label>
                <select 
                  data-testid="vote-event" 
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-rose-500/50" 
                  value={eventId} 
                  onChange={e=>setEventId(e.target.value)}
                >
                  <option value="">Event wählen...</option>
                  {events.map(e => (
                    <option key={e.id} value={e.id}>{e.title || e.name || e.id}</option>
                  ))}
                </select>
              </div>

              {/* Category Selection */}
              <div>
                <label className="text-xs text-rose-200/60 block mb-1.5">Kategorie</label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map(cat => {
                    const Icon = cat.icon;
                    const isSelected = category === cat.value;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setCategory(cat.value)}
                        className={`p-3 rounded-lg border text-sm flex flex-col items-center gap-1.5 transition-colors ${
                          isSelected 
                            ? 'bg-rose-600/20 border-rose-500/50 text-rose-200' 
                            : 'bg-white/5 border-white/10 text-rose-200/60 hover:bg-white/10'
                        }`}
                      >
                        <Icon size={18} className={isSelected ? cat.color : 'text-rose-200/40'} />
                        <span className="text-xs">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
                <input type="hidden" data-testid="vote-category" value={category} />
              </div>

              {/* Player Selection */}
              <div>
                <label className="text-xs text-rose-200/60 block mb-1.5">Spieler nominieren</label>
                <select 
                  data-testid="vote-nominee" 
                  className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-rose-500/50" 
                  value={nominee} 
                  onChange={e=>setNominee(e.target.value)}
                >
                  <option value="">Spieler auswählen...</option>
                  {players.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>

              {/* Submit Button */}
              <Button 
                data-testid="vote-submit" 
                type="submit" 
                disabled={!eventId || !nominee}
                className="w-full bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 py-5"
              >
                <Star size={16} className="mr-2" />
                Stimme abgeben
              </Button>
            </form>

            {/* Status Message */}
            {status && (
              <div 
                className={`mt-4 p-3 rounded-lg text-sm text-center ${
                  status.includes('gespeichert') 
                    ? 'bg-green-900/30 border border-green-500/30 text-green-300' 
                    : 'bg-red-900/30 border border-red-500/30 text-red-300'
                }`}
                data-testid="vote-status"
              >
                {status}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
